import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import { AdaptiveGapVisual } from "@/components/AdaptiveGap";
import { usePageTitle } from "@/lib/usePageTitle";

function Eyebrow({ children, testid }) {
  return (
    <Reveal>
      <p className="text-xs tracking-[0.25em] uppercase text-fog" data-testid={testid}>
        {children}
      </p>
    </Reveal>
  );
}

const CONDITIONS = [
  {
    n: "01",
    name: "Speed",
    text: "Technological development cycles are accelerating, compressing the time available for people, organisations and institutions to adjust.",
  },
  {
    n: "02",
    name: "Complexity",
    text: "Technologies increasingly operate as interconnected systems, increasing the cognitive and organisational demands required to understand and use them effectively.",
  },
  {
    n: "03",
    name: "Opacity",
    text: "The mechanisms shaping contemporary technological systems are often difficult for the people operating within them to see or understand.",
  },
];

const GAP_ZONES = [
  {
    n: "04",
    name: "Equilibrium",
    text: "Technology and adaptive capacity evolve in a relatively balanced way.",
  },
  {
    n: "05",
    name: "Emerging imbalance",
    text: "Demands increase and early signals begin to appear: skills mismatches, growing cognitive demands and difficulties integrating new systems into existing structures.",
  },
  {
    n: "06",
    name: "Critical disequilibrium",
    text: "The distance has widened enough that individuals, organisations or institutions actively struggle to integrate technological systems effectively.",
  },
];

const CHAPTERS = [...CONDITIONS, ...GAP_ZONES];

/* One continuous scroll sequence: the three conditions, then the three
   zones, with the framework diagram evolving alongside. */
function ConceptSequence() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.55", "end 0.7"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(CHAPTERS.length - 1, Math.max(0, Math.floor(v * CHAPTERS.length))));
  });

  return (
    <div ref={ref} className="relative" data-testid="concept-sequence">
      <div className="md:grid md:grid-cols-12 md:gap-10">
        <div className="md:col-span-5 mb-12 md:mb-0">
          <div className="md:sticky md:top-28">
            <AdaptiveGapVisual phase={active} className="w-full max-w-md hidden md:block" />
            <AdaptiveGapVisual phase={active} mobile className="w-full md:hidden" />
            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-serif text-2xl text-ember">{CHAPTERS[active].n}</span>
              <span className="text-xs tracking-[0.2em] uppercase text-fog">{CHAPTERS[active].name}</span>
            </div>
            <p className="mt-3 text-[10px] tracking-[0.15em] uppercase text-fog/60">
              After Puica (2026), Technology-Human Equilibrium Framework
            </p>
          </div>
        </div>

        <div className="md:col-span-7">
          <Eyebrow testid="conditions-eyebrow">02 · The conditions</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6">
            <ScrollMaskedLine>The demands are changing.</ScrollMaskedLine>
          </h2>
          <Reveal delay={0.1} className="mt-6 max-w-xl">
            <p className="text-fog leading-relaxed">
              The Technology-Human Disequilibrium framework identifies three structural conditions
              that can widen the distance between technological expansion and human adaptive
              capacity.
            </p>
          </Reveal>

          {CONDITIONS.map((c, i) => (
            <div
              key={c.n}
              data-testid={`condition-${c.n}`}
              className={`min-h-[32vh] md:min-h-[52vh] flex flex-col justify-center transition-opacity duration-700 ${
                i === active ? "opacity-100" : "opacity-35"
              }`}
            >
              <span className="text-[11px] tracking-[0.25em] uppercase text-ember">{c.n} · {c.name}</span>
              <p className="mt-4 font-serif text-xl md:text-2xl leading-snug tracking-tight max-w-xl">{c.text}</p>
            </div>
          ))}

          <div className="pt-10 md:pt-16">
            <Eyebrow testid="gap-eyebrow">03 · The adaptive gap</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 max-w-xl">
              <ScrollMaskedLine>What happens when capability begins to fall behind?</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.1} className="mt-6 max-w-xl">
              <p className="text-fog leading-relaxed">
                Together, these conditions can create what the framework describes as the adaptive
                gap: the growing distance between technological expansion and the capacity of
                human systems to adapt.
              </p>
            </Reveal>
          </div>

          {GAP_ZONES.map((z, i) => (
            <div
              key={z.n}
              data-testid={`gap-zone-${i}`}
              className={`min-h-[32vh] md:min-h-[52vh] flex flex-col justify-center transition-opacity duration-700 ${
                i + 3 === active ? "opacity-100" : "opacity-35"
              }`}
            >
              <span className="text-[11px] tracking-[0.25em] uppercase text-ember">{z.name}</span>
              <p className="mt-4 font-serif text-xl md:text-2xl leading-snug tracking-tight max-w-xl">{z.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ResearchNote01() {
  usePageTitle("Research Note 01: The Adaptive Gap | Mentheris", "We keep accelerating technology. But what is accelerating our capacity to adapt? A short Mentheris research note.");
  return (
    <main className="pt-24 md:pt-32" data-testid="research-note-01">
      {/* ---------------- METADATA + HERO ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-16 md:pb-20">
        <MaskedLine delay={0.1}>
          <span className="text-xs tracking-[0.3em] uppercase text-fog" data-testid="note-label">
            Research Note · 01
          </span>
        </MaskedLine>
        <Reveal delay={0.25} className="mt-6">
          <div className="flex flex-wrap gap-x-10 gap-y-3 text-[11px] tracking-[0.2em] uppercase text-fog" data-testid="note-meta">
            <span>May 2026</span>
            <span>Technology-Human Disequilibrium</span>
            <span className="text-fog/60">An idea we are examining.</span>
          </div>
        </Reveal>

        <div className="mt-12 max-w-4xl">
          <h1 className="font-serif font-medium tracking-tighter leading-[1.05] text-4xl md:text-6xl" data-testid="note-headline">
            <MaskedLine delay={0.35}>We keep accelerating technology.</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="italic text-ember">But what is accelerating our capacity to adapt?</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.85} className="mt-8 max-w-xl space-y-5">
            <p className="text-base md:text-lg text-fog leading-relaxed">
              Technology investment tends to focus on what a system can do: greater automation,
              faster processes, more sophisticated capabilities.
            </p>
            <p className="text-base md:text-lg text-fog leading-relaxed">
              But every expansion of technological capability also creates new demands on the
              people and organisations expected to work with it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- 01 THE ASSUMPTION ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="assumption-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="assumption-eyebrow">01 · The assumption</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl">
              <ScrollMaskedLine>We tend to assume adaptation will follow.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5">
              <p className="text-fog leading-relaxed">
                When technology changes, people are often expected to adapt.
              </p>
              <p className="text-fog leading-relaxed">
                That expectation is rarely stated explicitly. It operates quietly inside
                implementation plans, organisational strategies and wider narratives of
                technological progress: introduce the technology, and adaptation will eventually
                follow.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-12 md:pl-10">
              <p className="font-serif italic text-2xl md:text-4xl leading-[1.3] tracking-tight text-ink/85" data-testid="assumption-pullquote">
                But adaptation is not automatic.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- 02 THE CONDITIONS + 03 THE ADAPTIVE GAP ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="conditions-section">
        <ConceptSequence />
      </section>

      {/* ---------------- CENTRAL STATEMENT ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32" data-testid="statement-section">
        <h2 className="font-serif font-medium tracking-tighter leading-[1.1] text-4xl md:text-6xl max-w-4xl">
          <ScrollMaskedLine>The problem is not technological</ScrollMaskedLine>
          <ScrollMaskedLine delay={0.15}>progress itself.</ScrollMaskedLine>
        </h2>
        <Reveal delay={0.3} className="mt-8">
          <p className="font-serif italic text-xl md:text-2xl text-fog leading-relaxed max-w-2xl" data-testid="statement-sub">
            It is the assumption that adaptation will simply follow.
          </p>
        </Reveal>
      </section>

      {/* ---------------- A QUESTION TO CARRY FORWARD ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="question-section">
        <Eyebrow testid="question-eyebrow">A question to carry forward</Eyebrow>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 max-w-3xl leading-[1.15]">
          <ScrollMaskedLine>Before asking what a technology can do,</ScrollMaskedLine>
          <ScrollMaskedLine delay={0.12}>
            <span className="italic text-ember">what needs to become possible around it?</span>
          </ScrollMaskedLine>
        </h2>
        <Reveal delay={0.2} className="mt-8 max-w-2xl">
          <p className="text-fog leading-relaxed">
            A technology may be technically ready long before the people, structures and
            institutions around it are ready to absorb what it changes.
          </p>
        </Reveal>
        <div className="mt-12 space-y-8 max-w-3xl">
          {[
            "What new capabilities will this technology require?",
            "What needs to change around it for its value to be realised sustainably?",
          ].map((q, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-4 items-baseline" data-testid={`carry-question-${i}`}>
              <span className="md:col-span-2 text-[11px] tracking-[0.25em] uppercase text-fog">0{i + 1}</span>
              <h3 className="md:col-span-10 font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight text-ink/85">
                <ScrollMaskedLine delay={i * 0.1}>{q}</ScrollMaskedLine>
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- RESEARCH CONTEXT ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="context-section">
        <Reveal className="max-w-2xl">
          <p className="text-sm text-fog leading-relaxed">
            This Research Note develops one idea from the Technology-Human Disequilibrium research
            programme and the first Mentheris Research Dialogue held in May 2026.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            <Link
              to="/research"
              data-testid="context-thd-link"
              className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              Explore Technology-Human Disequilibrium
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/session/session1"
              data-testid="context-dialogue-link"
              className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              Research Dialogue 01
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ---------------- CONTINUE THE INQUIRY ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="inquiry-section">
        <Eyebrow testid="inquiry-eyebrow">Continue the inquiry</Eyebrow>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 max-w-3xl">
          <ScrollMaskedLine>Seeing this pattern in your work or research?</ScrollMaskedLine>
        </h2>
        <Reveal delay={0.15} className="mt-6 max-w-2xl">
          <p className="text-fog leading-relaxed">
            If you are working with technological transformation, human capability or
            organisational adaptation and recognise this tension in practice, I would be
            interested in hearing your perspective.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <a
            href="mailto:contact@mentheris.com"
            data-testid="inquiry-cta"
            className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300 mt-10"
          >
            Continue the conversation
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>

      {/* ---------------- AUTHOR + CONTINUE EXPLORING ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-8 pb-24 md:pb-32" data-testid="note-footer">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-fog/70 mb-4">Author</p>
            <p className="font-serif text-xl tracking-tight">Dr. Elena Puică</p>
            <p className="mt-1 text-sm text-fog">Founder & Principal Researcher, Mentheris</p>
            <Link
              to="/about"
              data-testid="author-link"
              className="group inline-flex items-center gap-1.5 mt-4 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              About the author
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="md:col-span-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-fog/70 mb-6">Continue exploring</p>
            {[
              {
                tag: "Research Note · 02",
                title: "What happens when AI removes the path to becoming an expert?",
                to: "/research-note-02",
                testid: "explore-note-02",
              },
              {
                tag: "Research programme",
                title: "Technology-Human Disequilibrium",
                to: "/research",
                testid: "explore-thd",
              },
              {
                tag: "Research Dialogue · 01",
                title: "The adaptation gap: when technology outpaces human capacity",
                to: "/session/session1",
                testid: "explore-dialogue",
              },
            ].map((r) => (
              <Reveal key={r.testid}>
                <Link
                  to={r.to}
                  data-testid={r.testid}
                  className="group relative flex items-baseline justify-between gap-6 py-5"
                >
                  <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-fog mb-1.5">{r.tag}</span>
                    <span className="font-serif text-lg md:text-xl leading-snug transition-transform duration-500 group-hover:translate-x-1 inline-block">
                      {r.title}
                    </span>
                  </span>
                  <ArrowRight size={15} className="shrink-0 text-fog transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
